import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';
import { ketNumberMap } from './ketNumbering';

const DefinitionRow = ({ question, questionNumber, answer, handleInputChange, showHint, isReviewMode, isHydrated }) => {
  const isExample = question.points === 0;
  // Render {{gap}} tokens as a visible blank (e.g. "p{{gap}}" → "p_____") —
  // the actual typing happens in the input below.
  const definitionText = question.question_text
    .replace(/\(Example\)/gi, '')
    .replace(/\{\{[^}]*\}\}/g, '_____')
    .trim();

  const isAnswerCorrect = question.is_correct === true || question.is_correct === 'true';
  const showIncorrectAnswerInReview = isReviewMode && !isExample && !isAnswerCorrect;

  return (
    <div className={`p-4 rounded-2xl border-2 transition-all ${isExample ? 'bg-blue-50/50 border-blue-100 dark:bg-blue-900/20 dark:border-blue-800' : 'bg-white border-slate-100 dark:bg-slate-800/50 dark:border-slate-700 hover:border-blue-200 shadow-sm'}`}>
      <div className="flex items-start gap-3">
        <span className={`shrink-0 font-black px-2.5 py-1 rounded-xl text-sm border shadow-sm mt-0.5 ${isExample ? 'text-white bg-amber-500 border-amber-500' : 'text-slate-400 bg-slate-50 dark:bg-slate-900 border-slate-100 dark:border-slate-800'}`}>
          {isExample ? 'Ví dụ' : `${questionNumber}.`}
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-slate-700 dark:text-slate-300 text-base leading-relaxed m-0">
            {definitionText}
          </p>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            {showIncorrectAnswerInReview ? (
              <div className="exercise-input-text inline-flex items-center bg-red-50 px-3 py-1.5 rounded-xl border border-red-100">
                {question.user_answer?.answer
                  ? <span className="text-red-500 line-through decoration-2 mr-2 font-bold">{question.user_answer.answer}</span>
                  : <span className="text-gray-400 mr-2 font-bold">__</span>
                }
                <span className="text-green-600 font-black">{question.accepted_answers[0]}</span>
              </div>
            ) : (
              <input
                type="text"
                value={isExample ? (question.accepted_answers && question.accepted_answers[0]) || '' : (showHint || (isReviewMode && isAnswerCorrect)) ? question.accepted_answers[0] : answer || ''}
                onChange={isExample ? undefined : (e) => handleInputChange(question.id, e.target.value)}
                readOnly={isExample || showHint || !isHydrated}
                disabled={isReviewMode || !isHydrated}
                className={`exercise-input-field exercise-input-text w-48 text-center py-2 font-black text-lg !rounded-xl ${isExample ? 'exercise-example-text' : ''} ${showHint && !isExample ? 'hint-input' : ''}`}
                autoComplete="off"
              />
            )}
            {isReviewMode && !isExample && (isAnswerCorrect
              ? <span className="correct-checkmark">✓</span>
              : <span className="incorrect-checkmark">✗</span>
            )}
            {isReviewMode && !isExample && <QuestionLearnButton question={question} />}
          </div>
        </div>
      </div>
    </div>
  );
};

const KetReadingPart6 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      // 1. Prioritize API data in review mode
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      // 2. Otherwise load from localStorage
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData && typeof savedData === 'object') {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId || isReviewMode) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, isReviewMode]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const sortedQuestions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);
  const exampleQuestion = sortedQuestions.find(q => q.points === 0);
  const mainQuestions = sortedQuestions.filter(q => q.points > 0);
  const numberMap = ketNumberMap(exerciseData);

  const handleInputChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  return (
    <div className="w-full h-full p-1 md:px-10 overflow-y-auto custom-scrollbar">
      <div className="max-w-3xl mx-auto w-full py-4 space-y-3">
        {exampleQuestion && (
          <DefinitionRow
            question={exampleQuestion}
            answer={answers[exampleQuestion.id]}
            handleInputChange={handleInputChange}
            isReviewMode={isReviewMode}
            isHydrated={isHydrated}
          />
        )}
        {mainQuestions.map((q) => (
          <DefinitionRow
            key={q.id}
            question={q}
            questionNumber={numberMap[q.id]}
            answer={answers[q.id]}
            handleInputChange={handleInputChange}
            showHint={showHint}
            isReviewMode={isReviewMode}
            isHydrated={isHydrated}
          />
        ))}
      </div>
    </div>
  );
};

export default KetReadingPart6;
